/*
  File name: index.js
    Program Description:
    - This file indicates all possible routes that can be accessed by the user
    in the system (excluding requests on backend)
    - It handles all routes and navigation handlers to navigate to the specified
    page upon entering the page.
*/

import Vue from 'vue'
import VueRouter from 'vue-router'
import Login from '@/views/general/Login.vue'
import PassReset from '@/views/general/PassReset.vue'
import { PageNotFound } from 'maroon-vue-components'
import store from '../store'

Vue.use(VueRouter)

const routes = [
  {
    path: '/',
    redirect: 'login'
  },
  {
    path: '*',
    redirect: '/404'
  },
  {
    path: '/sso',
    name: 'SSO',
    component: function () {
      return import(/* webpackChunkName: "about" */ '../views/general/SSO.vue')
    }
  },
  {
    path: '/redirect',
    name: 'Redirect',
    component: function () {
      return import(/* webpackChunkName: "about" */ '../views/general/Redirect.vue')
    }
  },
  {
    path: '/404',
    name: 'PageNotFound',
    component: PageNotFound
  },
  {
    path: '',
    name: 'Guest',
    component: function () {
      return import('../views/general/Guest')
    },
    children: [
      {
        path: '/login',
        name: 'Login',
        component: Login,
        meta: {
          title: 'Login Page - TKL',
          metaTags: [
            {
              name: 'description',
              content: 'Login to the TKL Web App'
            }
          ]
        },
        beforeEnter: (to, from, next) => {
          if (store.state.lookups.isInternalLogin) next({ name: 'InternalLogin' })
          else if (to.fullPath === '/login') next()
          else next({ name: 'PageNotFound' })
        }
      },
      {
        path: '/internal/login',
        name: 'InternalLogin',
        beforeEnter: (to, from, next) => {
          store.commit('lookups/setOpts', {
            type: 'isInternalLogin',
            payload: true
          })
          next()
        },
        component: Login
      },
      {
        path: '/passreset',
        name: 'PassReset',
        component: PassReset,
        beforeEnter: (to, from, next) => {
          if (to.fullPath === '/PassReset') next()
          else next({ name: 'PageNotFound' })
        }
      },
      {
        path: '/signup',
        name: 'Signup',
        component: function () {
          return import('../views/general/Signup.vue')
        },
        beforeEnter: (to, from, next) => {
          if (to.fullPath === '/signup') next()
          else next({ name: 'PageNotFound' })
        }
      },
      {
        path: '/user/password/reset',
        name: 'PassResetLink',
        component: function () {
          return import(/* webpackChunkName: "about" */ '../views/general/PassResetLink.vue')
        }
      }
    ]
  },
  {
    path: '/admin',
    name: 'Admin',
    component: function () {
      return import(/* webpackChunkName: "about" */ '../views/adminPages/Admin.vue')
    },
    children: [
      {
        path: 'dashboard',
        name: 'AdminDashboard',
        component: function () {
          return import(/* webpackChunkName: "about" */ '../views/adminPages/AdminDashboard.vue')
        }
      },
      {
        path: 'users/:userRole',
        name: 'AdminUsersList',
        component: function () {
          return import(/* webpackChunkName: "about" */ '../views/adminPages/Users.vue')
        }
      },
      {
        path: 'users/:userRole/:operation',
        name: 'UserProfile',
        component: function () {
          return import(/* webpackChunkName: "about" */ '../views/adminPages/UserProfile.vue')
        }
      },
      {
        path: 'users/:userRole/:operation/:id',
        name: 'UserProfileView',
        component: function () {
          return import(/* webpackChunkName: "about" */ '../views/adminPages/UserProfile.vue')
        }
      },
      {
        path: 'account',
        name: 'AdminAccount',
        component: function () {
          return import(/* webpackChunkName: "about" */ '../views/adminPages/AdminProfile.vue')
        }
      },
      {
        path: 'causeOfIncident',
        name: 'CauseOfIncidentTable',
        component: function () {
          return import('../views/adminPages/causeOfIncident/CauseOfIncidentTable.vue')
        }
      },
      {
        path: 'sector',
        name: 'SectorTable',
        component: function () {
          return import('../views/adminPages/sector/SectorTable.vue')
        }
      },
      {
        path: 'notificationTypes',
        name: 'NotificationTypesTable',
        component: function () {
          return import('../views/adminPages/notificationTypes/NotificationTypesTable.vue')
        }
      },
      {
        path: 'dbnStatus',
        name: 'DbnStatusTable',
        component: function () {
          return import('../views/adminPages/dbnStatus/DbnStatusTable.vue')
        }
      },
      {
        path: 'natureOfBreach',
        name: 'NatureOfBreachTable',
        component: function () {
          return import('../views/adminPages/natureOfBreach/NatureOfBreachTable.vue')
        }
      },
      {
        path: 'recordType',
        name: 'RecordTypeTable',
        component: function () {
          return import('../views/adminPages/recordType/RecordTypeTable.vue')
        }
      },
      {
        path: 'dataBreach/:dataTab',
        name: 'DataBreachTable',
        component: function () {
          return import('../views/adminPages/dataBreach/DataBreachTable.vue')
        }
      },
      {
        path: 'dataBreach/0',
        name: 'DataBreachTableAssign',
        component: function () {
          return import('../views/adminPages/dataBreach/DataBreachTable.vue')
        }
      },
      {
        path: 'settings',
        name: 'DataBreachSettings',
        component: function () {
          return import(/* webpackChunkName: "about" */ '../views/adminPages/dataBreach/DataBreachSettings.vue')
        }
      },
      {
        path: 'dataBreach/:dataTab/:operation',
        name: 'DataBreachCreate',
        component: function () {
          return import('../views/adminPages/dataBreach/DataBreachForm.vue')
        }
      },
      {
        path: 'dataBreach/:dataTab/:operation/:id',
        name: 'DataBreachView',
        component: function () {
          return import('../views/adminPages/dataBreach/DataBreachForm.vue')
        }
      },
      {
        path: 'dataBreach/:dataTab/:operation/:id/logs',
        name: 'DataBreachActivityLogs',
        component: function () {
          return import('../views/adminPages/dataBreach/DataBreachActivityLogs.vue')
        }
      },
      {
        path: 'fbner/:operation',
        name: 'FinalBreachEvaluationCreate',
        component: function () {
          return import('../views/adminPages/finalBreachEvaluation/FinalBreachEvaluationForm.vue')
        }
      },
      {
        path: 'fbner/:role/:operation/:id',
        name: 'FinalBreachEvaluationView',
        component: function () {
          return import('../views/adminPages/finalBreachEvaluation/FinalBreachEvaluationForm.vue')
        }
      },
      {
        path: 'dataBreach/:dataTab/:dbn/pbnef/list',
        name: 'PBNEFLanding',
        component: function () {
          return import('../views/adminPages/pbnef/PBNEFLanding.vue')
        }
      },
      {
        path: 'dataBreach/:dataTab/:dbn/pbnef/drafts',
        name: 'PBNEFDrafts',
        component: function () {
          return import('../views/adminPages/pbnef/PBNEFDrafts.vue')
        }
      },
      {
        path: 'dataBreach/:dataTab/:dbn/pbnef/:operation',
        name: 'PBNEFCreate',
        component: function () {
          return import('../views/adminPages/pbnef/PBNEForm.vue')
        }
      },
      {
        path: 'dataBreach/:dataTab/:dbn/pbnef/:operation/:id',
        name: 'PBNEFView',
        component: function () {
          return import('../views/adminPages/pbnef/PBNEForm.vue')
        }
      },
      {
        path: 'securityIncident/',
        name: 'SecurityIncidentTable',
        component: function () {
          return import('../views/adminPages/securityIncident/SecurityIncidentTable.vue')
        }
      },
      {
        path: 'securityIncident/:dataTab/:operation',
        name: 'SecurityIncidentCreate',
        component: function () {
          return import('../views/adminPages/securityIncident/SecurityIncidentForm.vue')
        }
      },
      {
        path: 'securityIncident/:dataTab/:operation/:id',
        name: 'SecurityIncidentView',
        component: function () {
          return import('../views/adminPages/securityIncident/SecurityIncidentForm.vue')
        }
      },
      {
        path: 'notifications',
        name: 'notifications',
        component: function () {
          return import('../views/adminPages/Notifications.vue')
        }
      },
      {
        path: 'reports',
        name: 'Reports',
        component: function () {
          return import('../views/general/Reports.vue')
        }
      },
      {
        path: 'reportsAsir',
        name: 'ReportsAsir',
        component: function () {
          return import('../views/general/ReportsAsir.vue')
        }
      },
      {
        path: 'analytics/:dataTab',
        name: 'Analytics',
        component: function () {
          return import('../views/general/AnalyticsTable.vue')
        },
        props: true
      },
      {
        path: 'analytics/:dataTab/ratings/:operation/:role/:year/:semester/:id',
        name: 'AnalyticsForm',
        component: function () {
          return import('../views/adminPages/analytics/AnalyticsForm.vue')
        },
        props: true
      }
    ]
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.VUE_APP_PREFIX || '',
  routes
})

Vue.mixin({
  beforeRouteEnter (to, from, next) {
    next(async vm => {
      await vm.$s.ready
      const { userRole, userType } = vm.$s
      vm.$store.commit('lookups/setOpts', {
        type: 'isInternalLogin',
        payload: userType === 'Internal'
      })
      if (!userRole.includes('guest')) {
        if (to.fullPath.includes('admin')) {
          next({ path: to.fullPath })
        } else {
          if (!from.name || from.fullPath === '/') {
            next({ name: 'AdminDashboard' })
          }
        }
      }
    })
  }
})

export default router
